import daylight_profile_icon from "src/assets/images/daylight-profile-icon.png";
import type { AsyncStatus } from "../../components/types";
import { ConfigurationSliderCard } from "../../general-configuration/components/configuration-cards.component";
import { GetConfigButton } from "../../components/get-config-button.component";
import { ActionModeSelector } from "../../components/action-mode-selector.component";
import type { ActionMode, DaylightProfileValues } from "../../components/types";
import { sanitizeNumericInput } from "../hooks/use-daylight-profile-validation.hook";

type DaylightProfileFormProps = {
  values: DaylightProfileValues;
  onValuesChange: (values: DaylightProfileValues) => void;
  commandStatus: AsyncStatus;
  commandCountdown: number;
  isLoadingCommandResult: boolean;
  onGetConfig: () => void;
  onViewCommandResult: () => void;
  isEnabled: boolean;
  zoneOptions: { id: number; name: string }[];
  actionModeOptions: { id: number; name: string }[];
  errors: Record<string, string>;
  onTouchField?: (fieldName: string) => void;
};

export function DaylightProfileForm({
  values,
  onValuesChange,
  commandStatus,
  commandCountdown,
  isLoadingCommandResult,
  onGetConfig,
  onViewCommandResult,
  isEnabled,
  zoneOptions,
  actionModeOptions,
  errors,
  onTouchField,
}: DaylightProfileFormProps) {
  const handleChange = (field: keyof DaylightProfileValues, value: any) => {
    onValuesChange({ ...values, [field]: value });
    onTouchField?.(field);
  };

  const handleActionModeChange = (field: "lowerAction" | "upperAction", mode: ActionMode) => {
    onValuesChange({ ...values, [field]: mode });
    onTouchField?.(field);
  };

  const handleTimeChange = (field: "activeStartTime" | "activeEndTime", value: string) => {
    onValuesChange({ ...values, [field]: value });
    onTouchField?.(field);
  };

  const handleZoneChange = (value: number | null) => {
    const selectedZone = zoneOptions.find(z => z.id === value);
    onValuesChange({
      ...values,
      zoneId: value,
      zoneName: selectedZone?.name || null,
    });
    onTouchField?.("zoneId");
  };

  const handleBrightnessChange = (field: "baseBrightness" | "fullBrightness", value: any) => {
    const sanitized = sanitizeNumericInput(value, 0, 100);
    handleChange(field, sanitized);
  };

  const handleLuxInputChange = (field: "lowerLuxThreshold" | "upperLuxThreshold", raw: string) => {
    const sanitized = sanitizeNumericInput(raw, 0, 1000);
    handleChange(field, sanitized);
  };

  const stripLeadingZeros = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const raw = input.value;
    if (raw.length > 1 && /^0\d+$/.test(raw)) {
      const sanitized = raw.replace(/^0+/, "") || "0";
      input.value = sanitized;
    }
  };

  return (
    <section className="configuration-card configuration-card--daylight">
      <div className="configuration-card__header configuration-card__header--between">
        <div className="configuration-card__header-left">
          <span className="configuration-card__icon">
            <img src={daylight_profile_icon} alt="daylight profile icon" />
          </span>
          <h3 className="configuration-card__title">Daylight Profile</h3>
        </div>
        <div className="configuration-card__header-actions">
          <GetConfigButton
            status={commandStatus}
            isLoading={isLoadingCommandResult}
            onGetConfig={onGetConfig}
            onViewResult={onViewCommandResult}
            label="Get Daylight Config"
            countdown={commandCountdown}
          />

          <label className="switch">
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(event) => handleChange("isEnabled", event.target.checked)}
            />
            <span className="switch__slider" />
          </label>
        </div>
      </div>

      <div className="configuration-subcard">
        <div className="configuration-subcard__header">
          <h3 className="configuration-subcard__title">Active Time</h3>
        </div>
        <div className="daylight-time-group">
          <div className="daylight-time-field">
            <label className="daylight-time-label">Start</label>
            <input
              type="time"
              className="profile-form__input"
              value={values.activeStartTime || ""}
              onChange={(e) => handleTimeChange("activeStartTime", e.target.value)}
            />
          </div>
          <span className="daylight-time-separator">-</span>
          <div className="daylight-time-field">
            <label className="daylight-time-label">End</label>
            <input
              type="time"
              className="profile-form__input"
              value={values.activeEndTime || ""}
              onChange={(e) => handleTimeChange("activeEndTime", e.target.value)}
            />
          </div>
        </div>
        {errors.activeStartTime && (
          <span className="profile-form__error">{errors.activeStartTime}</span>
        )}
        {errors.activeEndTime && (
          <span className="profile-form__error">{errors.activeEndTime}</span>
        )}
      </div>

      <div className="configuration-subcard">
        <div className="configuration-subcard__header">
          <h3 className="configuration-subcard__title">Light Intensity</h3>
        </div>
        <div className="configuration-mini-grid">
          <ConfigurationSliderCard
            title="Base Brightness"
            subtitle="Minimum brightness level"
            suffix="%"
            value={values.baseBrightness}
            min={0}
            max={100}
            step={1}
            onValueChange={(value) => handleBrightnessChange("baseBrightness", value)}
            onClick={() => handleBrightnessChange("baseBrightness", 0)}
            icon=""
            error={errors.baseBrightness}
          />
          <ConfigurationSliderCard
            title="Full Brightness"
            subtitle="Maximum brightness level"
            suffix="%"
            value={values.fullBrightness}
            min={0}
            max={100}
            step={1}
            onValueChange={(value) => handleBrightnessChange("fullBrightness", value)}
            onClick={() => handleBrightnessChange("fullBrightness", 0)}
            icon=""
            error={errors.fullBrightness}
          />
        </div>
      </div>

      <div className="configuration-subcard">
        <div className="configuration-subcard__header">
          <h3 className="configuration-subcard__title">Photocell Thresholds</h3>
        </div>
        <div className="configuration-mini-grid">
          <div className="sensor-threshold">
            <div className="sensor-threshold__header">
              <div>
                <div className="sensor-threshold__title">Lower Lux Threshold</div>
                <div className="sensor-threshold__subtitle">Triggers on dim daylight</div>
              </div>
              <div className="sensor-threshold__value">
                <label className="configuration-mini-card__value-input">
                  <input
                    type="number"
                    min={0}
                    max={1000}
                    step={1}
                    inputMode="numeric"
                    value={values.lowerLuxThreshold ?? 0}
                    onChange={(e) => handleLuxInputChange("lowerLuxThreshold", e.target.value)}
                    onInput={stripLeadingZeros}
                  />
                  <span>lux</span>
                </label>
                {errors.lowerLuxThreshold && (
                  <span className="profile-form__error">{errors.lowerLuxThreshold}</span>
                )}
              </div>
            </div>
            <input
              className="configuration-range"
              type="range"
              min={0}
              max={1000}
              step={1}
              value={values.lowerLuxThreshold ?? 0}
              onChange={(e) => {
                const sanitized = sanitizeNumericInput(e.target.value, 0, 1000);
                handleChange("lowerLuxThreshold", sanitized);
              }}
            />
            <div className="sensor-threshold__footer">
              <span className="sensor-threshold__label">Action:</span>
              <ActionModeSelector
                options={actionModeOptions}
                value={values.lowerAction}
                onChange={(mode) => handleActionModeChange("lowerAction", mode)}
              />
            </div>
            {errors.lowerAction && (
              <span className="profile-form__error">{errors.lowerAction}</span>
            )}
          </div>

          <div className="sensor-threshold">
            <div className="sensor-threshold__header">
              <div>
                <div className="sensor-threshold__title">Upper Lux Threshold</div>
                <div className="sensor-threshold__subtitle">Triggers on bright daylight</div>
              </div>
              <div className="sensor-threshold__value">
                <label className="configuration-mini-card__value-input">
                  <input
                    type="number"
                    min={0}
                    max={1000}
                    step={1}
                    inputMode="numeric"
                    value={values.upperLuxThreshold ?? 0}
                    onChange={(e) => handleLuxInputChange("upperLuxThreshold", e.target.value)}
                    onInput={stripLeadingZeros}
                  />
                  <span>lux</span>
                </label>
                {errors.upperLuxThreshold && (
                  <span className="profile-form__error">{errors.upperLuxThreshold}</span>
                )}
              </div>
            </div>
            <input
              className="configuration-range"
              type="range"
              min={0}
              max={1000}
              step={1}
              value={values.upperLuxThreshold ?? 0}
              onChange={(e) => {
                const sanitized = sanitizeNumericInput(e.target.value, 0, 1000);
                handleChange("upperLuxThreshold", sanitized);
              }}
            />
            <div className="sensor-threshold__footer">
              <span className="sensor-threshold__label">Action:</span>
              <ActionModeSelector
                options={actionModeOptions}
                value={values.upperAction}
                onChange={(mode) => handleActionModeChange("upperAction", mode)}
              />
            </div>
            {errors.upperAction && (
              <span className="profile-form__error">{errors.upperAction}</span>
            )}
          </div>
        </div>
      </div>

      <div className="configuration-subcard">
        <div className="configuration-subcard__header">
          <h3 className="configuration-subcard__title">Zone</h3>
        </div>
        <div className="profile-form__select-wrap">
          <div className="profile-form__select-control">
            <select
              className="profile-form__input profile-form__select"
              value={values.zoneId || ""}
              onChange={(e) => handleZoneChange(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Select Zone</option>
              {zoneOptions.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name}
                </option>
              ))}
            </select>
          </div>
          {errors.zoneId && (
            <span className="profile-form__error">{errors.zoneId}</span>
          )}
        </div>
      </div>
    </section>
  );
}
